import { ProfileData } from "../firebase/profileService";
import { Experience } from "../firebase/experienceService";

/**
 * Derives recruiter-facing facts from existing profile/experience data WITHOUT
 * adding new CMS fields. Pure presentation helpers (see reference/NeededPlan.md §7).
 */

export interface Stat {
  value: string;
  label: string;
}

const textPool = (profile?: Partial<ProfileData>): string =>
  [profile?.shortBio, profile?.bio, profile?.resumeSummary, profile?.skillsDescription]
    .filter(Boolean)
    .join(" \n ");

/** "13+" extracted from "13+ years of experience"; falls back to years since earliest role. */
export const deriveYearsOfExperience = (
  profile?: Partial<ProfileData>,
  experiences: Experience[] = []
): string | null => {
  const match = textPool(profile).match(/(\d{1,2})\s*\+?\s*years?/i);
  if (match) return `${match[1]}+`;

  const startYears = experiences
    .map((e) => parseInt((e.startDate || "").slice(0, 4), 10))
    .filter((y) => !Number.isNaN(y));
  if (startYears.length) {
    const earliest = Math.min(...startYears);
    const years = new Date().getFullYear() - earliest;
    if (years > 0) return `${years}+`;
  }
  return null;
};

/** Current role = the experience that has no end date ("Present"). */
export const deriveCurrentRole = (
  experiences: Experience[] = []
): { position: string; company: string } | null => {
  const current = experiences.find((e) => !e.endDate || e.endDate.trim() === "");
  if (current) return { position: current.position, company: current.company };
  if (experiences[0]) return { position: experiences[0].position, company: experiences[0].company };
  return null;
};

/** Builds the impact-stats band from numbers already present in the profile prose. */
export const deriveStats = (
  profile?: Partial<ProfileData>,
  experiences: Experience[] = []
): Stat[] => {
  const pool = textPool(profile);
  const stats: Stat[] = [];

  const years = deriveYearsOfExperience(profile, experiences);
  if (years) stats.push({ value: years, label: "Years Experience" });

  const engineers = pool.match(/(\d{1,3})\s*\+?\s*engineers/i);
  if (engineers) stats.push({ value: `${engineers[1]}+`, label: "Engineers Led" });

  const users = pool.match(/([\d.,]+\s*[KkMm]?)\s*\+?\s*(?:active\s*)?users/i);
  if (users) stats.push({ value: `${users[1].replace(/\s+/g, "").toUpperCase()}+`, label: "Users Reached" });

  const load = pool.match(/load\s*times?\s*by\s*(\d{1,2})\s*%/i);
  if (load) stats.push({ value: `${load[1]}%`, label: "Faster Load Times" });
  else {
    const coverage = pool.match(/coverage\s*from\s*\d{1,3}%\s*to\s*(\d{1,3})%/i);
    if (coverage) stats.push({ value: `${coverage[1]}%`, label: "Test Coverage" });
  }

  return stats;
};
